import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api, Certification, InsurancePolicy } from '../../lib/api';
import { uploadDocument } from '../../lib/documentService';
import { useTrades } from '../../hooks/useTrades';
import { MultiSelectDropdown } from '../shared/MultiSelectDropdown';
import {
  ArrowLeft,
  Save,
  CheckCircle2,
  Upload,
  FileText,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertCircle
} from 'lucide-react';

interface ProfileCompletionProps {
  onBack: () => void;
}

export const ProfileCompletion: React.FC<ProfileCompletionProps> = ({ onBack }) => {
  const { profile, refreshProfile } = useAuth();
  const { trades, loading: tradesLoading } = useTrades();
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [expandedSection, setExpandedSection] = useState<string>('basic');
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    company_name: profile?.company_name || '',
    phone: profile?.phone || '',
    location: profile?.location || '',
    bio: profile?.bio || ''
  });

  const [selectedTrades, setSelectedTrades] = useState<string[]>(profile?.trades || []);

  const [licenses, setLicenses] = useState<Certification[]>([]);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [insurancePolicies, setInsurancePolicies] = useState<InsurancePolicy[]>([]);
  const [uploading, setUploading] = useState<string | null>(null);

  useEffect(() => {
    if (profile?.trades) {
      setSelectedTrades(profile.trades);
    }
  }, [profile?.trades]);

  useEffect(() => {
    loadDocuments();
  }, [profile]);

  const loadDocuments = async () => {
    if (!profile) return;

    try {
      const [licenses, certs, insurance] = await Promise.all([
        api.get<Certification[]>('/certifications', { user_id: profile.id, type: 'license' }),
        api.get<Certification[]>('/certifications', { user_id: profile.id, type: 'certification' }),
        api.get<InsurancePolicy[]>('/insurance_policies', { user_id: profile.id })
      ]);

      setLicenses(licenses || []);
      setCertifications(certs || []);
      setInsurancePolicies(insurance || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const autoSave = useCallback(async () => {
    if (!profile) return;

    setSaving(true);
    try {
      await api.patch('/profiles/me', {
        fullName: formData.full_name,
        companyName: formData.company_name,
        phone: formData.phone,
        location: formData.location,
        trades: selectedTrades,
        bio: formData.bio,
        updatedAt: new Date().toISOString()
      });

      setLastSaved(new Date());
      await refreshProfile();
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  }, [formData, selectedTrades, profile, refreshProfile]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.full_name) {
        autoSave();
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [formData, selectedTrades, autoSave]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const uploadFile = async (file: File, documentType: 'license' | 'certification' | 'insurance' | 'other' = 'other'): Promise<string | null> => {
    if (!profile) return null;

    try {
      const result = await uploadDocument(file, profile.id, documentType);
      if (result.success && result.url) {
        return result.url;
      }
      throw new Error(result.error || 'Upload failed');
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  };

  const addLicense = async () => {
    if (!profile) return;

    const newLicense: Partial<Certification> = {
      user_id: profile.id,
      type: 'license',
      name: '',
      issuer: '',
      number: '',
      verified: false
    };

    try {
      const data = await api.post<Certification>('/certifications', newLicense);
      if (data) setLicenses([...licenses, data]);
    } catch (error) {
      console.error('Error adding license:', error);
    }
  };

  const updateLicense = async (id: string, updates: Partial<Certification>) => {
    try {
      await api.patch(`/certifications/${id}`, updates);
      setLicenses(licenses.map(l => l.id === id ? { ...l, ...updates } : l));
    } catch (error) {
      console.error('Error updating license:', error);
    }
  };

  const deleteLicense = async (id: string) => {
    try {
      await api.delete(`/certifications/${id}`);
      setLicenses(licenses.filter(l => l.id !== id));
    } catch (error) {
      console.error('Error deleting license:', error);
    }
  };

  const handleLicenseFileUpload = async (id: string, file: File) => {
    setUploading(id);
    const url = await uploadFile(file, 'license');
    if (url) {
      await updateLicense(id, { document_url: url });
    }
    setUploading(null);
  };

  const addCertification = async () => {
    if (!profile) return;

    const newCert: Partial<Certification> = {
      user_id: profile.id,
      type: 'certification',
      name: '',
      issuer: '',
      verified: false
    };

    try {
      const data = await api.post<Certification>('/certifications', newCert);
      if (data) setCertifications([...certifications, data]);
    } catch (error) {
      console.error('Error adding certification:', error);
    }
  };

  const updateCertification = async (id: string, updates: Partial<Certification>) => {
    try {
      await api.patch(`/certifications/${id}`, updates);
      setCertifications(certifications.map(c => c.id === id ? { ...c, ...updates } : c));
    } catch (error) {
      console.error('Error updating certification:', error);
    }
  };

  const deleteCertification = async (id: string) => {
    try {
      await api.delete(`/certifications/${id}`);
      setCertifications(certifications.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error deleting certification:', error);
    }
  };

  const handleCertFileUpload = async (id: string, file: File) => {
    setUploading(id);
    const url = await uploadFile(file, 'certification');
    if (url) {
      await updateCertification(id, { document_url: url });
    }
    setUploading(null);
  };

  const addInsurancePolicy = async () => {
    if (!profile) return;

    const newPolicy: Partial<InsurancePolicy> = {
      user_id: profile.id,
      policy_type: 'liability',
      provider: '',
      start_date: new Date().toISOString().split('T')[0],
      expiry_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'active'
    };

    try {
      const data = await api.post<InsurancePolicy>('/insurance_policies', newPolicy);
      if (data) setInsurancePolicies([...insurancePolicies, data]);
    } catch (error) {
      console.error('Error adding insurance:', error);
    }
  };

  const updateInsurance = async (id: string, updates: Partial<InsurancePolicy>) => {
    try {
      await api.patch(`/insurance_policies/${id}`, updates);
      setInsurancePolicies(insurancePolicies.map(p => p.id === id ? { ...p, ...updates } : p));
    } catch (error) {
      console.error('Error updating insurance:', error);
    }
  };

  const deleteInsurance = async (id: string) => {
    try {
      await api.delete(`/insurance_policies/${id}`);
      setInsurancePolicies(insurancePolicies.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting insurance:', error);
    }
  };

  const handleInsuranceFileUpload = async (id: string, file: File) => {
    setUploading(id);
    const url = await uploadFile(file, 'insurance');
    if (url) {
      await updateInsurance(id, { document_url: url });
    }
    setUploading(null);
  };

  const calculateProgress = () => {
    let completed = 0;
    let total = 6;

    if (formData.full_name) completed++;
    if (formData.phone) completed++;
    if (formData.location) completed++;
    if (selectedTrades.length > 0) completed++;
    if (formData.bio) completed++;
    if (licenses.length > 0 || certifications.length > 0 || insurancePolicies.length > 0) completed++;

    return Math.round((completed / total) * 100);
  };

  const handleSubmitForVerification = async () => {
    if (!profile) return;

    try {
      await api.patch('/profiles/me', {
        verification_status: 'pending',
        updated_at: new Date().toISOString()
      });

      await api.post('/notifications', {
        user_id: profile.id,
        type: 'compliance',
        title: 'Verification Submitted',
        message: 'Your profile has been submitted for verification. We will review your documents and notify you once the review is complete.',
        read: false
      });

      await refreshProfile();

      setShowSubmitConfirm(false);
      onBack();
    } catch (error) {
      console.error('Error submitting for verification:', error);
    }
  };

  const progress = calculateProgress();
  const hasAnyDocuments = licenses.length > 0 || certifications.length > 0 || insurancePolicies.length > 0;

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? '' : section);
  };

  const SectionHeader: React.FC<{ title: string; id: string; completed: boolean }> = ({ title, id, completed }) => (
    <button
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
    >
      <div className="flex items-center gap-3">
        {completed && <CheckCircle2 className="w-5 h-5 text-green-600" />}
        <span className="font-semibold text-slate-900">{title}</span>
      </div>
      {expandedSection === id ? (
        <ChevronUp className="w-5 h-5 text-slate-400" />
      ) : (
        <ChevronDown className="w-5 h-5 text-slate-400" />
      )}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sticky top-0 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 z-10 shadow-lg">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="relative max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-white hover:text-slate-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-300">
              {saving ? (
                <>
                  <Save className="w-4 h-4 animate-pulse" />
                  <span>Saving...</span>
                </>
              ) : lastSaved ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>Saved {lastSaved.toLocaleTimeString()}</span>
                </>
              ) : null}
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Complete Your Profile</h1>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-white/20 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-sm font-semibold text-white min-w-[3rem]">{progress}%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <SectionHeader
          title="Basic Information"
          id="basic"
          completed={!!(formData.full_name && formData.phone && formData.location)}
        />
        {expandedSection === 'basic' && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 shadow-sm hover:shadow-md transition-shadow">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Company Name
              </label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="ABC Contracting"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Los Angeles, CA"
              />
            </div>
          </div>
        )}

        <SectionHeader
          title="Trade Information"
          id="trade"
          completed={!!(selectedTrades.length > 0 && formData.bio)}
        />
        {expandedSection === 'trade' && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 shadow-sm hover:shadow-md transition-shadow">
            <MultiSelectDropdown
              options={trades.map(trade => ({
                id: trade.id,
                name: trade.name,
                category: trade.category
              }))}
              selected={selectedTrades}
              onChange={setSelectedTrades}
              label="Select Your Trades"
              placeholder={tradesLoading ? 'Loading trades...' : 'Select one or more trades'}
              required
              disabled={tradesLoading}
              groupByCategory
            />

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                About You <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                rows={4}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                placeholder="Describe your experience, specialties, and what makes you a great contractor..."
              />
              <p className="text-xs text-slate-500 mt-1">
                This will be visible to general contractors looking to hire
              </p>
            </div>
          </div>
        )}

        <SectionHeader
          title="Licenses"
          id="licenses"
          completed={licenses.length > 0}
        />
        {expandedSection === 'licenses' && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 shadow-sm hover:shadow-md transition-shadow">
            {licenses.map((license) => (
              <div key={license.id} className="border border-slate-200 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <button
                    onClick={() => deleteLicense(license.id)}
                    className="p-1 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>

                <input
                  type="text"
                  value={license.name}
                  onChange={(e) => updateLicense(license.id, { name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="License name"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={license.issuer || ''}
                    onChange={(e) => updateLicense(license.id, { issuer: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    placeholder="Issuing authority"
                  />
                  <input
                    type="text"
                    value={license.number || ''}
                    onChange={(e) => updateLicense(license.id, { number: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    placeholder="License number"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Issue Date</label>
                    <input
                      type="date"
                      value={license.issue_date || ''}
                      onChange={(e) => updateLicense(license.id, { issue_date: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Expiry Date</label>
                    <input
                      type="date"
                      value={license.expiry_date || ''}
                      onChange={(e) => updateLicense(license.id, { expiry_date: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    />
                  </div>
                </div>

                {license.document_url ? (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">Document uploaded</span>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    {uploading === license.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                        <span className="text-sm text-slate-600">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">Upload document</span>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleLicenseFileUpload(license.id, file);
                          }}
                          className="hidden"
                        />
                      </>
                    )}
                  </label>
                )}
              </div>
            ))}

            <button
              onClick={addLicense}
              className="w-full py-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span className="font-medium">Add License</span>
            </button>
          </div>
        )}

        <SectionHeader
          title="Certifications"
          id="certifications"
          completed={certifications.length > 0}
        />
        {expandedSection === 'certifications' && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 shadow-sm hover:shadow-md transition-shadow">
            {certifications.map((cert) => (
              <div key={cert.id} className="border border-slate-200 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <button
                    onClick={() => deleteCertification(cert.id)}
                    className="p-1 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>

                <input
                  type="text"
                  value={cert.name}
                  onChange={(e) => updateCertification(cert.id, { name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="Certification name"
                />

                <input
                  type="text"
                  value={cert.issuer || ''}
                  onChange={(e) => updateCertification(cert.id, { issuer: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="Issuing organization"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Issue Date</label>
                    <input
                      type="date"
                      value={cert.issue_date || ''}
                      onChange={(e) => updateCertification(cert.id, { issue_date: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Expiry Date</label>
                    <input
                      type="date"
                      value={cert.expiry_date || ''}
                      onChange={(e) => updateCertification(cert.id, { expiry_date: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    />
                  </div>
                </div>

                {cert.document_url ? (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">Document uploaded</span>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    {uploading === cert.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                        <span className="text-sm text-slate-600">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">Upload document</span>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleCertFileUpload(cert.id, file);
                          }}
                          className="hidden"
                        />
                      </>
                    )}
                  </label>
                )}
              </div>
            ))}

            <button
              onClick={addCertification}
              className="w-full py-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span className="font-medium">Add Certification</span>
            </button>
          </div>
        )}

        <SectionHeader
          title="Insurance"
          id="insurance"
          completed={insurancePolicies.length > 0}
        />
        {expandedSection === 'insurance' && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 shadow-sm hover:shadow-md transition-shadow">
            {insurancePolicies.map((policy) => (
              <div key={policy.id} className="border border-slate-200 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <button
                    onClick={() => deleteInsurance(policy.id)}
                    className="p-1 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>

                <select
                  value={policy.policy_type}
                  onChange={(e) => updateInsurance(policy.id, { policy_type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  <option value="liability">General Liability</option>
                  <option value="workers_comp">Workers Compensation</option>
                  <option value="tools">Tools & Equipment</option>
                </select>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={policy.provider}
                    onChange={(e) => updateInsurance(policy.id, { provider: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    placeholder="Insurance provider"
                  />
                  <input
                    type="text"
                    value={policy.policy_number || ''}
                    onChange={(e) => updateInsurance(policy.id, { policy_number: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    placeholder="Policy number"
                  />
                </div>

                <input
                  type="number"
                  value={policy.coverage_amount || ''}
                  onChange={(e) => updateInsurance(policy.id, { coverage_amount: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="Coverage amount"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={policy.start_date}
                      onChange={(e) => updateInsurance(policy.id, { start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">Expiry Date</label>
                    <input
                      type="date"
                      value={policy.expiry_date}
                      onChange={(e) => updateInsurance(policy.id, { expiry_date: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    />
                  </div>
                </div>

                {policy.document_url ? (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">Document uploaded</span>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    {uploading === policy.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                        <span className="text-sm text-slate-600">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">Upload document</span>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleInsuranceFileUpload(policy.id, file);
                          }}
                          className="hidden"
                        />
                      </>
                    )}
                  </label>
                )}
              </div>
            ))}

            <button
              onClick={addInsurancePolicy}
              className="w-full py-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span className="font-medium">Add Insurance Policy</span>
            </button>
          </div>
        )}

        <div className="sticky bottom-4 bg-white border border-slate-200 rounded-xl p-4 shadow-lg">
          <button
            onClick={() => setShowSubmitConfirm(true)}
            disabled={!hasAnyDocuments}
            className={`w-full py-3 rounded-lg font-semibold transition-colors ${
              hasAnyDocuments
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            Submit for Verification
          </button>
          {!hasAnyDocuments && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Add at least one document to submit for verification
            </p>
          )}
        </div>
      </div>

      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Submit for Verification?</h3>
                <p className="text-sm text-slate-600">
                  Your profile and documents will be reviewed by our team. This typically takes 1-2 business days. You'll receive a notification once the review is complete.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSubmitConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitForVerification}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
